"use client"
import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  type PlanId,
  type ResolvedBranchesData,
  scrollPlanSelectorIntoView,
} from "@/lib/branchTypes"
import {
  type SetSelectedPathOptions,
  buildParentMap,
  buildPathToNode,
  getInitialSelection,
  mergeVisitedNodeIds,
  stabilizeScrollToElementId,
  syncSelectedPathToUrl,
} from "../data/playbookStateUtils"

const clearPendingTargetIfCurrent = (
  pendingScrollTargetId: string,
  setPendingScrollTargetId: Dispatch<SetStateAction<string | null>>
) => {
  setPendingScrollTargetId(currentNodeId => {
    return currentNodeId === pendingScrollTargetId ? null : currentNodeId
  })
}

const getTargetElementId = (
  branchData: ResolvedBranchesData,
  pendingScrollTargetId: string
) => {
  const targetNode = branchData.nodes[pendingScrollTargetId]
  return targetNode?.anchor || pendingScrollTargetId
}

// On initial load with ?choices=..., the branch panel hydrates progressively,
// so the target node's <div id> appears late and keeps drifting as content
// above lays out. stabilizeScrollToElementId handles that (long find budget +
// per-frame re-aim until stable); here we just supply the top-aligned default
// and fall back to the plan selector if the node never mounts.
const usePendingSelectionScroll = (
  branchData: ResolvedBranchesData,
  pendingScrollTargetId: string | null,
  setPendingScrollTargetId: Dispatch<SetStateAction<string | null>>,
  scrollRequestIdRef: MutableRefObject<number>
) => {
  useEffect(() => {
    if (!pendingScrollTargetId) return
    const elementId = getTargetElementId(branchData, pendingScrollTargetId)
    stabilizeScrollToElementId(elementId, scrollRequestIdRef, {}, (found) => {
      if (!found) scrollPlanSelectorIntoView()
      clearPendingTargetIfCurrent(pendingScrollTargetId, setPendingScrollTargetId)
    })
  }, [branchData, pendingScrollTargetId, setPendingScrollTargetId, scrollRequestIdRef])
}

export const usePlaybookSelectionState = (branchData: ResolvedBranchesData) => {
  const [selectedPath, setSelectedPathState] = useState<string[]>(
    () => getInitialSelection(branchData, null).selectedPath
  )
  const [pendingScrollTargetId, setPendingScrollTargetId] = useState<string | null>(null)

  // ?choices= (deep link into a chosen plan path) is applied AFTER mount from
  // the raw URL, not via useSearchParams — a render-time useSearchParams read
  // here would make the statically-rendered / route prerender its (null)
  // Suspense fallback, shipping blank HTML instead of the scenario text.
  // Deep-linked visitors briefly see the default path before the selection
  // applies and the pending scroll kicks in.
  const appliedChoicesParamRef = useRef(false)
  useEffect(() => {
    if (appliedChoicesParamRef.current) return
    appliedChoicesParamRef.current = true
    const choicesParam = new URLSearchParams(window.location.search).get("choices")
    if (!choicesParam) return
    const selection = getInitialSelection(branchData, choicesParam)
    setSelectedPathState(selection.selectedPath)
    // Hash takes precedence over ?choices=: if the URL has a hash on initial
    // load, useAnchorDeepLink will handle expansion + scroll, and we must not
    // also kick off a smooth scroll-to-the-last-choice (they fight and the
    // smooth one wins visually, leaving you partway).
    const hasInitialHash = window.location.hash && window.location.hash.length > 1
    if (!hasInitialHash) setPendingScrollTargetId(selection.pendingScrollTargetId)
  }, [branchData])
  const [hoveredPlan, setHoveredPlan] = useState<PlanId | null>(null)
  const [hoveredBranchTargetId, setHoveredBranchTargetId] = useState<string | null>(null)
  const [, setVisitedNodeIds] = useState<string[]>([])
  const scrollRequestIdRef = useRef(0)
  const parentMap = useMemo(() => buildParentMap(branchData), [branchData])

  usePendingSelectionScroll(
    branchData,
    pendingScrollTargetId,
    setPendingScrollTargetId,
    scrollRequestIdRef
  )

  const setSelectedPath = useCallback((
    path: string[],
    options?: SetSelectedPathOptions
  ) => {
    setSelectedPathState(path)
    setVisitedNodeIds(prev => mergeVisitedNodeIds(prev, path))
    if (options?.updateUrl ?? true) syncSelectedPathToUrl(path)
    setPendingScrollTargetId(options?.scrollTargetNodeId ?? null)
  }, [])

  const selectNode = useCallback((
    nodeId: string,
    options?: { scroll?: boolean, scrollTargetId?: string }
  ) => {
    const path = buildPathToNode(nodeId, parentMap)
    if (path.length === 0) return
    setSelectedPath(path, {
      scrollTargetNodeId: options?.scroll === false
        ? null
        : options?.scrollTargetId || nodeId
    })
  }, [parentMap, setSelectedPath])

  return {
    hoveredBranchTargetId,
    hoveredPlan,
    selectNode,
    selectedPath,
    setHoveredBranchTargetId,
    setHoveredPlan,
    setSelectedPath,
  }
}
